import {CertificateDetailsModel} from "@/api-client";
import Handlebars from "handlebars";
import {formatDate} from "@/formatting/dateFormatter";

Handlebars.registerHelper('helperMissing', function(...args: any[]) {
    const options = args[args.length - 1] as {
        name: string;
        data: { root: Record<string, unknown> };
        lookupProperty: (parent: unknown, key: string) => unknown;
    };
    const rootValue = options.lookupProperty(options.data.root, options.name);
    if (rootValue === null) {
        return "";
    }
    return `UNKNOWN(${options.name})`;
});
import emptyOfficialSignaturePlaceholder from "@/assets/placeholders/single-pixel.gif";

export interface PrintTemplateRenderOptions {
    transparentSignatureUrl?: string | null;
    transparentOfficialSignatureUrl?: string | null;
}

export async function toAbsoluteApiUrl(url: string | null | undefined): Promise<string | null> {
    if (!url)
        return null;
    if (!url.startsWith("/"))
        return url;

    const settings = (await import('@/main')).settings;
    return settings.misApiBaseUrl + url;
}

export default async function renderCertificatePrintTemplate(
    certificate: CertificateDetailsModel,
    printTemplateUrl: string,
    qrCodeUrl: string,
    page1?:string | null,
    page2?:string | null,
    options?: PrintTemplateRenderOptions,
): Promise<string> {
    // Handle relative URLs by prepending the API base URL
    let fullUrl = printTemplateUrl;
    let page1Full = null;
    let page2Full = null;
    if (printTemplateUrl.startsWith('/')) {
        // Get the API base URL from settings
        const settings = (await import('@/main')).settings;
        fullUrl = settings.misApiBaseUrl + printTemplateUrl;
    }

    if(page1?.startsWith("/") || page2?.startsWith("/")){
        const settings = (await import('@/main')).settings;
        page1Full = settings.misApiBaseUrl + page1;
        page2Full = settings.misApiBaseUrl + page2;
    }

    const signatureUrl = await toAbsoluteApiUrl(options?.transparentSignatureUrl) ?? certificate.signatureUrl;
    const officialSignatureUrl = await toAbsoluteApiUrl(options?.transparentOfficialSignatureUrl)
        ?? certificate.officialSignature?.url
        ?? emptyOfficialSignaturePlaceholder;

    const printTemplateFile = await fetch(fullUrl, { method: 'GET' });
    const templateContent = await printTemplateFile.text();

    // Extract text literals from the template if present
    let literals = {};
    const literalsMatch = templateContent.match(/<script type="application\/json" id="template-literals">([\s\S]*?)<\/script>/);
    if (literalsMatch && literalsMatch[1]) {
        try {
            literals = JSON.parse(literalsMatch[1].trim());
        } catch (e) {
            console.warn('Failed to parse template literals:', e);
        }
    }

    // Parse certificate-level text literals (e.g. endorsement data for Soodustusluba)
    let certLiterals: Record<string, string> = {};
    if (certificate.textLiterals) {
        try {
            certLiterals = JSON.parse(certificate.textLiterals);
        } catch (e) {
            console.warn('Failed to parse certificate text literals:', e);
        }
    }

    const template = Handlebars.compile(templateContent);
    /*eslint-disable */
    return template({
        SEERIANR : certificate.documentNumber,
        TUNNISTUSENR: certificate.endorsementDocumentNumber ?? "",
        TUNNISTUSNIMI: certificate.endorsementTypeLegalNameEst ?? "",
        TUNNISTUSNAME: certificate.endorsementTypeLegalNameEng ?? "",
        PERENIMI: certificate.lastName,
        EESNIMI: certificate.firstName,
        SYNNIKPV: formatDate(certificate.birthDate),
        ISIKUKOOD: certificate.personalCode,
        photo: certificate.photoUrl,
        signature: signatureUrl,
        KLOPP: formatDate(certificate.validUntil),
        VAKPV: formatDate(certificate.issuedOn),
        AMETIISIK: certificate.officialSignature?.signerFullName,
        official_signature: officialSignatureUrl,
        QRKOOD: qrCodeUrl,
        PAGE1: page1Full,
        PAGE2: page2Full,
        accessToken: certificate.accessToken,
        LAEVANIMI: certificate.shipNames?.join(", ") ?? "",
        GT: certificate.shipGrossTonnage?.toString() ?? certificate.shipGrossTonnages?.[0]?.toString() ?? "",
        GTENG: certificate.shipGrossTonnage?.toString() ?? certificate.shipGrossTonnages?.[0]?.toString() ?? "",
        GTANKER: certificate.tankerGrossTonnage?.toString() ?? "",
        REEGEL: certificate.stcwRuleCodes?.join(", ") ?? "",
        SOIDULIINID: certificate.shippingLineNames?.join(", ") ?? "",
        SADAM: certificate.harbourName ?? "",
        KOOL: certificate.educationalInstitutionName ?? "",
        KOOLENG: certificate.educationalInstitutionNameEng ?? "",
        KOOLITUSEKUUPAEV: formatDate(certificate.trainingDate),
        AMETENG: certificate.positionNamesEng?.join(", ") ?? "",
        AMETEST: certificate.positionNames?.join(", ") ?? "",
        PIIRKONNAD: certificate.pilotageAreaNames?.join(", ") ?? "",
        PIIRKONNADENG: certificate.pilotageAreaNamesEng?.join(", ") ?? "",
        LITERALS: certLiterals, // Make certificate-level literals available to Handlebars
        literals: literals, // Make literals available to Handlebars
    })
    /*eslint-enable */
}